/**
 * Solana Balance Fetching Hook
 * 
 * Uses Privy's embedded wallet to get the user's Solana wallet address
 * and fetches SOL balance + SPL token balances
 */

import { useState, useEffect, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';

export interface SolanaTokenBalance {
  mint: string;
  symbol: string;
  name: string;
  balance: string;
  balanceRaw: bigint;
  decimals: number;
  logo?: string;
  priceUSD?: number;
  valueUSD?: number;
  isNative?: boolean;
}

export interface SolanaBalanceResult {
  address: string | null;
  balances: SolanaTokenBalance[];
  totalValueUSD: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Solana RPC endpoints - using QuickNode from env var as primary, with public fallbacks
// Priority: QuickNode (VITE_SOLANA_RPC_URL) > Public Node > Official mainnet > Other public RPCs
const getSolanaRpcEndpoints = (): string[] => {
  const quicknodeUrl = import.meta.env.VITE_SOLANA_RPC_URL;
  const endpoints = quicknodeUrl 
    ? [quicknodeUrl] // QuickNode first if configured
    : [];
  
  // Add public fallbacks
  endpoints.push(
    'https://solana.publicnode.com', // Public Node - good CORS support
    'https://api.mainnet-beta.solana.com', // Official mainnet
    'https://solana-api.projectserum.com', // Serum
    'https://rpc.ankr.com/solana' // Ankr public RPC
  );
  
  return endpoints;
};

const SOLANA_RPC_ENDPOINTS = getSolanaRpcEndpoints();

// Cache the working connection
let cachedConnection: Connection | null = null;
let cachedConnectionTime: number = 0;
const CONNECTION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get a working RPC connection with caching
const getConnection = async (): Promise<Connection | null> => {
  // Return cached connection if still valid
  if (cachedConnection && Date.now() - cachedConnectionTime < CONNECTION_CACHE_DURATION) {
    return cachedConnection;
  }

  for (const rpc of SOLANA_RPC_ENDPOINTS) {
    try {
      const conn = new Connection(rpc, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 60000,
      });
      
      // Test connection with a lightweight call and timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      try {
        await conn.getSlot();
        clearTimeout(timeoutId);
        console.log('[Solana] Connected to RPC:', rpc);
        cachedConnection = conn;
        cachedConnectionTime = Date.now();
        return conn;
      } catch (e) {
        clearTimeout(timeoutId);
        throw e;
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      console.warn('[Solana] RPC failed:', rpc, '-', errorMessage);
    }
  }
  
  console.error('[Solana] All RPC endpoints failed');
  return null;
};

// Known SPL tokens to check (popular ones)
const KNOWN_SPL_TOKENS = [
  {
    mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  },
  {
    mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
    symbol: 'USDT',
    name: 'Tether USD',
    decimals: 6,
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB/logo.png',
  },
  {
    mint: 'So11111111111111111111111111111111111111112',
    symbol: 'WSOL',
    name: 'Wrapped SOL',
    decimals: 9,
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
  },
  {
    mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
    symbol: 'BONK',
    name: 'Bonk',
    decimals: 5,
    logo: 'https://arweave.net/hQiPZOsRZXGXBJd_82PhVdlM_hACsT_q6wqwf5cSY7I',
  },
  {
    mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
    symbol: 'JUP',
    name: 'Jupiter',
    decimals: 6,
    logo: 'https://static.jup.ag/jup/icon.png',
  },
  {
    mint: 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
    symbol: 'WIF',
    name: 'dogwifhat',
    decimals: 6,
    logo: 'https://bafkreibk3covs5ltyqxa272uodhculbr6kea6betidfwy3ajsav2vjzyum.ipfs.nftstorage.link',
  },
  {
    mint: 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So',
    symbol: 'mSOL',
    name: 'Marinade staked SOL',
    decimals: 9,
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So/logo.png',
  },
  {
    mint: '7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj',
    symbol: 'stSOL',
    name: 'Lido Staked SOL',
    decimals: 9,
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj/logo.png',
  },
];

// Cache for SOL price
let solPriceCache: { price: number; timestamp: number } | null = null;
const PRICE_CACHE_DURATION = 60000; // 1 minute

// Fetch SOL price from CoinGecko (free API)
const fetchSolPrice = async (): Promise<number> => {
  if (solPriceCache && Date.now() - solPriceCache.timestamp < PRICE_CACHE_DURATION) {
    return solPriceCache.price;
  }

  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd'
    );
    const data = await response.json();
    const price = data.solana?.usd || 0;
    solPriceCache = { price, timestamp: Date.now() };
    return price;
  } catch (error) {
    console.warn('[Solana] Failed to fetch SOL price:', error);
    return solPriceCache?.price || 0;
  }
};

// Fetch token price from Relay API
const fetchTokenPriceFromRelay = async (mint: string): Promise<number | null> => {
  try {
    // Relay uses chainId 792703809 for Solana
    const response = await fetch(
      `https://api.relay.link/currencies/token/price?address=${mint}&chainId=792703809&referrer=mavrk.ink`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.price || null;
  } catch {
    return null;
  }
};

// Fallback: Fetch Solana balances via Relay API (no CORS issues)
const fetchBalancesViaRelay = async (address: string): Promise<SolanaTokenBalance[]> => {
  try {
    console.log('[Solana] Fetching balances via Relay API for:', address);
    
    // Get SOL price first
    const solPrice = await fetchSolPrice();
    
    // Try Relay's user balance endpoint
    const response = await fetch(
      `https://api.relay.link/currencies/user/${address}?chainIds=792703809&referrer=mavrk.ink`
    );
    
    if (!response.ok) {
      console.warn('[Solana] Relay balance fetch failed:', response.status);
      return [];
    }
    
    const data = await response.json();
    const balances: SolanaTokenBalance[] = [];
    
    // Process Relay response
    if (Array.isArray(data)) {
      for (const token of data) {
        if (token.chainId === 792703809) { // Solana mainnet
          const isNative = token.address === 'So11111111111111111111111111111111111111111' || 
                          token.symbol === 'SOL' ||
                          token.address === '11111111111111111111111111111111';
          
          const balance = parseFloat(token.balance || '0') / Math.pow(10, token.decimals || 9);
          const price = token.usdPrice || (isNative ? solPrice : 0);
          
          if (balance > 0) {
            balances.push({
              mint: isNative ? 'native' : token.address,
              symbol: token.symbol || (isNative ? 'SOL' : 'SPL'),
              name: token.name || (isNative ? 'Solana' : 'SPL Token'),
              balance: balance.toString(),
              balanceRaw: BigInt(token.balance || '0'),
              decimals: token.decimals || 9,
              logo: token.logoURI || (isNative ? 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg' : undefined),
              priceUSD: price,
              valueUSD: balance * price,
              isNative,
            });
          }
        }
      }
    }
    
    console.log(`[Solana] Relay API returned ${balances.length} tokens`);
    return balances;
  } catch (error) {
    console.warn('[Solana] Relay balance fetch error:', error);
    return [];
  }
};

export function useSolanaBalances(
  enabled: boolean = true,
  externalAddress?: string | null
): SolanaBalanceResult {
  const { user, authenticated } = usePrivy();
  const [balances, setBalances] = useState<SolanaTokenBalance[]>([]);
  const [totalValueUSD, setTotalValueUSD] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Get Solana wallet from user's linked accounts
  // Privy stores Solana wallets in linkedAccounts with type 'wallet' and verifiedAt containing 'solana'
  // or specifically with chainType 'solana'
  // Cast to any[] to avoid union type issues with LinkedAccountWithMetadata
  const linkedAccounts = (user?.linkedAccounts || []) as any[];
  const solanaAccount = linkedAccounts.find(account => {
    // Check for Solana wallet type
    if (account.type === 'wallet' && account.chainType === 'solana') {
      return true;
    }
    // Also check if it's an embedded Solana wallet (Privy-specific type)
    if (account.type === 'solana_wallet') {
      return true;
    }
    return false;
  });
  
  // Use external address if provided, otherwise fall back to Privy linked account
  const solanaAddress = externalAddress || solanaAccount?.address || null;
  const allowExternal = !!externalAddress;

  const fetchBalances = useCallback(async () => {
    if (!solanaAddress || !enabled || (!authenticated && !allowExternal)) {
      setBalances([]);
      setTotalValueUSD(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[Solana] Fetching balances for:', solanaAddress);
      
      let fetchedBalances: SolanaTokenBalance[] = [];
      let usedFallback = false;
      
      // Try direct RPC first
      const connection = await getConnection();
      
      if (connection) {
        try {
          const publicKey = new PublicKey(solanaAddress);

          // 1. Fetch native SOL balance
          const solBalance = await connection.getBalance(publicKey);
          const solPrice = await fetchSolPrice();
          const solAmount = solBalance / LAMPORTS_PER_SOL;

          if (solBalance > 0) {
            fetchedBalances.push({
              mint: 'native',
              symbol: 'SOL',
              name: 'Solana',
              balance: solAmount.toFixed(9),
              balanceRaw: BigInt(solBalance),
              decimals: 9,
              logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg',
              priceUSD: solPrice,
              valueUSD: solAmount * solPrice,
              isNative: true,
            });
          }

          // 2. Fetch SPL token balances using getTokenAccountsByOwner
          try {
            const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
              programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
            });

            for (const account of tokenAccounts.value) {
              const parsedInfo = account.account.data.parsed?.info;
              if (!parsedInfo) continue;

              const mint = parsedInfo.mint;
              const balance = parsedInfo.tokenAmount?.uiAmount || 0;
              const decimals = parsedInfo.tokenAmount?.decimals || 0;
              const balanceRaw = parsedInfo.tokenAmount?.amount || '0';

              if (balance > 0) {
                // Find known token info
                const knownToken = KNOWN_SPL_TOKENS.find(t => t.mint === mint);
                
                // Fetch price from Relay
                const price = await fetchTokenPriceFromRelay(mint);

                fetchedBalances.push({
                  mint,
                  symbol: knownToken?.symbol || 'SPL',
                  name: knownToken?.name || 'SPL Token',
                  balance: balance.toString(),
                  balanceRaw: BigInt(balanceRaw),
                  decimals,
                  logo: knownToken?.logo,
                  priceUSD: price || 0,
                  valueUSD: price ? balance * price : 0,
                  isNative: false,
                });
              }
            }
          } catch (tokenError) {
            console.warn('[Solana] Failed to fetch SPL tokens via RPC:', tokenError);
          }
          
          console.log('[Solana] Successfully fetched via RPC');
        } catch (rpcError) {
          console.warn('[Solana] RPC balance fetch failed, trying Relay fallback:', rpcError);
          fetchedBalances = [];
          usedFallback = true;
        }
      } else {
        console.log('[Solana] No RPC connection, using Relay fallback');
        usedFallback = true;
      }
      
      // Fallback to Relay API if RPC failed (not just if empty)
      if (usedFallback) {
        const relayBalances = await fetchBalancesViaRelay(solanaAddress);
        if (relayBalances.length > 0) {
          fetchedBalances = relayBalances;
          console.log('[Solana] Used Relay API fallback');
        } else {
          // Both RPC and Relay failed, create a placeholder with zero balance
          const solPrice = await fetchSolPrice();
          fetchedBalances.push({
            mint: 'native',
            symbol: 'SOL',
            name: 'Solana',
            balance: '0',
            balanceRaw: BigInt(0),
            decimals: 9,
            logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg',
            priceUSD: solPrice,
            valueUSD: 0,
            isNative: true,
          });
          console.log('[Solana] Using placeholder balance (RPC and Relay both failed)');
        }
      } else if (fetchedBalances.length === 0) {
        // RPC succeeded but wallet has zero balance - add SOL placeholder
        const solPrice = await fetchSolPrice();
        fetchedBalances.push({
          mint: 'native',
          symbol: 'SOL',
          name: 'Solana',
          balance: '0',
          balanceRaw: BigInt(0),
          decimals: 9,
          logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg',
          priceUSD: solPrice,
          valueUSD: 0,
          isNative: true,
        });
        console.log('[Solana] Wallet has zero balance');
      }

      // Calculate total USD value
      const total = fetchedBalances.reduce((sum, t) => sum + (t.valueUSD || 0), 0);

      console.log(`[Solana] Found ${fetchedBalances.length} tokens with $${total.toFixed(2)} total value`);
      setBalances(fetchedBalances);
      setTotalValueUSD(total);

    } catch (err) {
      console.error('[Solana] Error fetching balances:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch Solana balances');
      
      // Still show placeholder even on error
      const solPrice = await fetchSolPrice();
      setBalances([{
        mint: 'native',
        symbol: 'SOL',
        name: 'Solana',
        balance: '0',
        balanceRaw: BigInt(0),
        decimals: 9,
        logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg',
        priceUSD: solPrice,
        valueUSD: 0,
        isNative: true,
      }]);
    } finally {
      setLoading(false);
    }
  }, [solanaAddress, enabled, authenticated, refreshTrigger, externalAddress, allowExternal]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const refetch = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return {
    address: solanaAddress,
    balances,
    totalValueUSD,
    loading,
    error,
    refetch,
  };
}

export default useSolanaBalances;

